'use client'

import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import type { CourseType } from '@/types/course-type'
import { EnumProductType } from '@/types/enum-product-type'
import { Button } from '@/components/ui/button'

const DESCRIPTION_MAX_LENGTH = 100

// Componente memoizado para cada item do curso
const CourseItem = memo(function CourseItemComponent({
  course,
  onCourseClick,
}: {
  course: CourseType
  onCourseClick: () => void
}) {
  return (
    <Link
      href={getDestinationRoute(
        course.product_type,
        course.external_id || course.id,
        course.public_url
      )}
      onClick={onCourseClick}
    >
      <Button
        className="h-auto w-full justify-start p-3 text-left"
        variant="ghost"
      >
        <div className="flex items-start gap-3">
          {course.image_url && (
            <Image
              alt={course.name}
              className="h-12 w-12 rounded object-cover"
              height={48}
              src={course.image_url}
              width={48}
            />
          )}
          <div className="flex-1 space-y-1">
            <p className="font-medium text-sm">{course.name}</p>
            <p className="text-muted-foreground text-xs">
              {course.description?.substring(0, DESCRIPTION_MAX_LENGTH)}
              {course.description &&
                course.description.length > DESCRIPTION_MAX_LENGTH &&
                '...'}
            </p>
          </div>
        </div>
      </Button>
    </Link>
  )
})

function getDestinationRoute(
  productType: string,
  productId: string,
  publicUrl: string
): string {
  const ProductRouteMap: Record<string, (id: string, url: string) => string> = {
    [EnumProductType.COURSE]: (id, url) => `/cursos/info/${id}/${url}`,
    [EnumProductType.WEBINAR]: (id, url) => `/cursos/info/${id}/${url}`,
    [EnumProductType.MENTORSHIP]: (id, url) => `/cursos/info/${id}/${url}`,
    [EnumProductType.PERSONALIZED]: (id, url) => `/cursos/info/${id}/${url}`,
    [EnumProductType.POSTGRADUATE]: id => `/pos-graduacoes/info/${id}`,
    [EnumProductType.TRACK]: (_, url) => `/cursos/info/${productId}/${url}`,
  }

  const routeBuilder = ProductRouteMap[productType.toLowerCase()]
  return routeBuilder?.(productId, publicUrl) ?? '/cursos'
}

interface SearchResultsProps {
  searchQuery: string
  results: CourseType[]
  isLoading?: boolean
  hasMoreResults?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
  onClose?: () => void
}

export const SearchResults = memo(function SearchResultsComponent({
  searchQuery,
  results,
  isLoading = false,
  hasMoreResults = false,
  isLoadingMore = false,
  onLoadMore,
  onClose,
}: SearchResultsProps) {
  const router = useRouter()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const lastItemRef = useRef<HTMLDivElement>(null)
  const [canScrollDown, setCanScrollDown] = useState(false)
  const [canScrollUp, setCanScrollUp] = useState(false)

  const handleCourseClick = useCallback(() => {
    onClose?.()
  }, [onClose])

  const handleSearchRedirect = useCallback(() => {
    if (searchQuery.trim()) {
      router.push(`/cursos?q=${encodeURIComponent(searchQuery.trim())}`)
      onClose?.()
    }
  }, [searchQuery, router, onClose])

  useEffect(() => {
    const lastItem = lastItemRef.current
    const shouldObserve =
      lastItem && hasMoreResults && !isLoadingMore && onLoadMore

    if (!shouldObserve) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          onLoadMore?.()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(lastItem)

    return () => {
      observer.disconnect()
    }
  }, [hasMoreResults, isLoadingMore, onLoadMore])

  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = container
    const isAtTop = scrollTop <= 0
    const isAtBottom = scrollTop >= scrollHeight - clientHeight - 1

    setCanScrollUp(!isAtTop)
    setCanScrollDown(!isAtBottom)
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    checkScrollability()
    container.addEventListener('scroll', checkScrollability)

    return () => {
      container.removeEventListener('scroll', checkScrollability)
    }
  }, [checkScrollability])
  if (isLoading) {
    return (
      <div className="rounded-lg bg-primary-50 p-4">
        <div className="flex items-center gap-2 text-primary-700 text-sm">
          <Search className="size-4" />
          <span>Buscando...</span>
        </div>
      </div>
    )
  }

  if (!searchQuery || results.length === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-primary-50 p-4">
        <div className="mb-2 flex flex-col gap-2">
          <div className="flex gap-2 text-left text-primary-700 text-sm">
            <span>Resultados para:</span>
            <span className="overflow-hidden text-ellipsis font-semibold">
              {searchQuery}
            </span>
          </div>
          <Button
            className="h-8 gap-2 text-xs"
            onClick={handleSearchRedirect}
            size="sm"
            variant="outline"
          >
            <Search className="size-3" />
            Ver todos
          </Button>
        </div>

        <p className="text-primary-300 text-sm">
          Role para baixo para ver mais resultados
        </p>
      </div>

      {results.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-muted-foreground text-sm">
            Cursos encontrados
          </h4>
          <div className="relative">
            {canScrollUp && (
              <div
                className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6"
                style={{
                  background: 'linear-gradient(to bottom, white, transparent)',
                }}
              >
                <div className="flex justify-center pt-1">
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            )}

            {canScrollDown && (
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6"
                style={{
                  background: 'linear-gradient(to top, white, transparent)',
                }}
              >
                <div className="flex justify-center pb-1">
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            )}

            <div
              className="max-h-96 space-y-1 overflow-y-auto"
              ref={scrollContainerRef}
            >
              {results.map((course, index) => (
                <div
                  key={course.reference}
                  ref={index === results.length - 1 ? lastItemRef : null}
                >
                  <CourseItem
                    course={course}
                    onCourseClick={handleCourseClick}
                  />
                </div>
              ))}

              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <div className="text-muted-foreground text-sm">
                    Carregando mais cursos...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
})
